/**
 * Reservations destination (DNKADM-0287, Phase 2b of the Concept-B nav rebuild) —
 * the tabbed parent that folds the standalone Confirmations queue into
 * Reservations as a **Pending** tab, so the two "what needs me?" surfaces live
 * at one nav destination:
 *  - **Reservations** (default) — the calendar ({@link ReservationsCalendar}),
 *    at the **index** path `/reservations`. Deliberately an index ROUTE, not an
 *    index redirect: the `/reservations?reservation=<uuid>` deep link
 *    (DNKADM-0147 — ActivityLog, the queue's "View on calendar", and the
 *    transaction tables) must arrive at the calendar with its query string
 *    exactly as before, with zero change to the deep-link handling.
 *  - **Pending** — the confirmation queue (`ConfirmationQueueScreen`), at
 *    `/reservations/pending`. `/confirmations` redirects here (router.tsx).
 *
 * This is a SECOND, OUTER level of view switching: the tab bar picks the
 * surface, while the calendar's own inner Now&Next/Day/Week toggle picks its
 * time window — the inner toggle is untouched (spec Non-Goals).
 *
 * Thin container: no data fetching beyond the Pending badge, which reads
 * `usePendingConfirmationCount` (DNKADM-0276) — the SAME event-synced source
 * as the nav badge and Now & Next's header, so the three can never disagree.
 * Tabs are `RouterLink`s (deep-linkable, back/forward works) with the
 * DNKADM-0179 tab↔panel ARIA wiring and `aria-current` on the active tab.
 */
import { Box, Chip, Tab, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import { resolveTokens } from '@/theme';
import { usePendingConfirmationCount } from '@/features/confirmations/usePendingConfirmationCount';

/** The two tabs in display/route order. `path` is absolute (index = `/reservations`). */
const TABS = [
  { key: 'calendar', path: '/reservations', label: 'Reservations' },
  { key: 'pending', path: '/reservations/pending', label: 'Pending' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function reservationsTabA11yProps(key: TabKey) {
  return {
    id: `reservations-tab-${key}`,
    'aria-controls': `reservations-tabpanel-${key}`,
  };
}

/**
 * Resolve the active tab from the current URL: anything under
 * `/reservations/pending` is the Pending tab; the index path `/reservations`
 * (and any other in-container path) is the calendar tab.
 */
function activeTabFrom(pathname: string): TabKey {
  if (pathname === '/reservations/pending' || pathname.startsWith('/reservations/pending/')) {
    return 'pending';
  }
  return 'calendar';
}

export function ReservationsScreen() {
  const tokens = resolveTokens(useTheme());
  const location = useLocation();
  const active = activeTabFrom(location.pathname);
  // The tenant-wide pending total (DNKADM-0276) — the tab is a third READER of
  // the shared hook (nav badge, Now & Next header), never a second count.
  const pendingCount = usePendingConfirmationCount() ?? 0;

  return (
    <Box>
      <Tabs
        value={active}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Reservations sections"
        sx={{ mb: 2, borderBottom: `1px solid ${tokens.lineSoft}` }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            component={RouterLink}
            to={t.path}
            aria-current={active === t.key ? 'page' : undefined}
            label={
              t.key === 'pending' && pendingCount > 0 ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  {t.label}
                  <Chip
                    label={pendingCount}
                    size="small"
                    sx={{
                      height: 22,
                      minWidth: 22,
                      bgcolor: tokens.warnBadgeBg,
                      color: tokens.warnBadgeInk,
                      fontWeight: 700,
                    }}
                  />
                </Box>
              ) : (
                t.label
              )
            }
            {...reservationsTabA11yProps(t.key)}
          />
        ))}
      </Tabs>

      <Box
        role="tabpanel"
        id={`reservations-tabpanel-${active}`}
        aria-labelledby={`reservations-tab-${active}`}
        tabIndex={0}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
